import { useState, useEffect, useCallback } from 'react';
import { api, ApiError } from '../lib/api';

/* ============================================================================
   THE OFFICE'S RELEASE QUEUE

   A parent is standing at a gate with a dead phone. A guard has raised a request.
   THIS SCREEN IS WHERE IT GETS DECIDED — and the guard cannot decide it.

   THE ONE THING THAT MATTERS ON THIS SCREEN:

     The guardian's number ON FILE, as a tap-to-call button.

   The office rings THAT number — not a number the person at the gate supplies,
   because a stranger would simply give their own. If the person at the gate
   answers the phone we already had on record, they are who they say they are.
   That is the check. Everything else is paperwork.
   ========================================================================== */

interface Release {
  id: string; studentId: string; studentName: string; admissionNumber: string; classLabel: string | null;
  personName: string; claimedRelation: string; reason: string; gateName: string | null;
  status: string; requestedAt: string;
  verifiedBy: string | null; verificationNote: string | null; refusalReason: string | null;
  guardianMobileOnFile: string | null;
  guardianNameOnFile: string | null;
}

const METHODS = [
  { id: 'CalledNumberOnFile', label: 'I rang the number on file and they answered', best: true },
  { id: 'PhotoId', label: 'Checked photo ID against the guardian name' },
  { id: 'KnownToStaff', label: 'Known to office staff personally' },
  { id: 'Other', label: 'Other (describe)' },
];

export function OfficeReleasesPage() {
  const [rows, setRows] = useState<Release[] | null>(null);
  const [deciding, setDeciding] = useState<Release | null>(null);

  const load = useCallback(() => {
    api.get<Release[]>('/api/office/releases').then(setRows).catch(() => setRows([]));
  }, []);

  useEffect(() => {
    load();
    // A parent is waiting at a gate. Poll.
    const t = setInterval(load, 15_000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div>
      <div className="page-head">
        <div>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Security</div>
          <h1 className="h-display" style={{ fontSize: 28 }}>Release requests</h1>
        </div>
      </div>

      {!rows ? (
        <div className="card" style={{ padding: 20 }}><span className="sk" style={{ width: '50%' }} /></div>
      ) : rows.length === 0 ? (
        <div className="empty">
          <h3>Nothing waiting</h3>
          <p>
            When a parent arrives without a valid pass, the guard raises a request here.
            You verify them, and only then can the child be released.
          </p>
        </div>
      ) : (
        <>
          <div className="pill pill-warn" style={{ padding: '10px 12px', borderRadius: 8, marginBottom: 16, justifyContent: 'flex-start' }}>
            <strong>{rows.length} {rows.length === 1 ? 'person is' : 'people are'} waiting at a gate.</strong>
          </div>

          {rows.map((r) => (
            <div key={r.id} className="card" style={{ padding: 20, marginBottom: 12, borderColor: 'var(--brand)' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
                <h2 className="h-display" style={{ fontSize: 19 }}>{r.studentName}</h2>
                <span className="mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>{r.admissionNumber}</span>
                {r.classLabel && <span className="pill pill-muted" style={{ fontSize: 11 }}>{r.classLabel}</span>}
                <span className="mono" style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-3)' }}>
                  waiting {minutesAgo(r.requestedAt)}
                </span>
              </div>

              {/* Who's at the gate — and note the wording: "claims to be". It isn't a fact yet. */}
              <div className="card" style={{ padding: 14, background: 'var(--surface-2)', marginBottom: 14 }}>
                <div style={{ fontSize: 14 }}>
                  <strong>{r.personName}</strong> claims to be the student's{' '}
                  <strong>{r.claimedRelation}</strong>.
                </div>
                <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 4 }}>
                  Reason given: {r.reason}
                  {r.gateName && ` · at ${r.gateName}`}
                </div>
              </div>

              {/* ⚠ THE CHECK. Ring the number WE already have — not one they gave the guard. */}
              {r.guardianMobileOnFile ? (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 6 }}>
                    Guardian on file
                  </div>
                  <a href={`tel:${r.guardianMobileOnFile}`} className="btn btn-accent"
                    style={{ textDecoration: 'none', width: '100%', justifyContent: 'center', padding: '14px' }}>
                    📞 Call {r.guardianNameOnFile ?? 'guardian'} — {r.guardianMobileOnFile}
                  </a>
                  <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 8, textAlign: 'center' }}>
                    Ring <strong>this</strong> number — not one they gave you. If the person at the gate
                    answers it, they are who they say they are.
                  </p>
                </div>
              ) : (
                <div className="pill pill-danger" style={{ padding: '10px 12px', borderRadius: 8, marginBottom: 14, display: 'block' }}>
                  <strong>No guardian number on file.</strong> You cannot verify by phone.
                  Check photo ID against the record before releasing.
                </div>
              )}

              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn" onClick={async () => {
                  const reason = window.prompt('Why are you refusing? (the guard has to tell them something)');
                  if (!reason?.trim()) return;
                  await api.post(`/api/office/releases/${r.id}/refuse`, { reason }).catch(() => {});
                  load();
                }}>
                  Refuse
                </button>
                <button className="btn btn-accent" style={{ marginLeft: 'auto' }}
                  onClick={() => setDeciding(r)}>
                  Verified — release the child
                </button>
              </div>
            </div>
          ))}
        </>
      )}

      {deciding && (
        <ApproveDialog release={deciding}
          onClose={() => setDeciding(null)}
          onDone={() => { setDeciding(null); load(); }} />
      )}
    </div>
  );
}

function ApproveDialog({ release: r, onClose, onDone }: {
  release: Release; onClose: () => void; onDone: () => void;
}) {
  const [method, setMethod] = useState('CalledNumberOnFile');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      await api.post(`/api/office/releases/${r.id}/approve`, {
        verifiedBy: method, note: note.trim() || null,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not release.');
    } finally { setBusy(false); }
  };

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <h2 className="h-display" style={{ fontSize: 19, marginBottom: 4 }}>
          Release {r.studentName}
        </h2>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginBottom: 16 }}>
          How did you satisfy yourself this is really the parent? This is recorded permanently.
        </p>

        {error && (
          <div className="pill pill-danger" style={{ padding: '9px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {METHODS.map((m) => (
            <label key={m.id} className="card" style={{
              padding: 12, display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer',
              borderColor: method === m.id ? 'var(--accent)' : undefined,
              background: method === m.id ? 'var(--surface-2)' : undefined,
            }}>
              <input type="radio" name="method" checked={method === m.id}
                onChange={() => setMethod(m.id)} style={{ marginTop: 3 }} />
              <div>
                <div style={{ fontSize: 14 }}>{m.label}</div>
                {m.best && (
                  <div style={{ fontSize: 11.5, color: 'var(--ok-ink, #1e7d4f)', marginTop: 2 }}>
                    Strongest check — a stranger can't answer someone else's phone.
                  </div>
                )}
              </div>
            </label>
          ))}
        </div>

        {method === 'Other' && (
          <div style={{ marginTop: 10 }}>
            <label className="label">Describe how you verified them</label>
            <input className="input" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        )}

        <div className="pill pill-warn" style={{ padding: '10px 12px', borderRadius: 8, marginTop: 14, display: 'block' }}>
          The parent will be notified immediately that their child has been collected.
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-accent" disabled={busy || (method === 'Other' && !note.trim())}
            onClick={save} style={{ marginLeft: 'auto' }}>
            {busy ? 'Releasing…' : 'Release the child'}
          </button>
        </div>
      </div>
    </div>
  );
}

function minutesAgo(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min`;
  return `${Math.floor(mins / 60)} hr`;
}
